import { Loader2, Send } from 'lucide-react';
import type { KnowledgeChatMessage, KnowledgePendingMutation } from '@/entities/knowledge';
import { useI18n } from '@/shared/i18n';
import { Button } from '@/shared/ui/shadcn/button';
import { ScrollArea } from '@/shared/ui/shadcn/scroll-area';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { cn } from '@/shared/lib/utils';

export function KnowledgeChatPanel({
  messages,
  pending,
  draft,
  busy,
  onDraftChange,
  onSend,
  onConfirm,
  onReject,
}: {
  messages: KnowledgeChatMessage[];
  pending: KnowledgePendingMutation[];
  draft: string;
  busy: boolean;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  onConfirm: () => void;
  onReject: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-background">
      <ScrollArea className="min-h-0 flex-1" viewportClassName="p-3">
        <div className="space-y-3">
        {messages.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t.knowledge.intro}</p>
        ) : null}
        {messages.map((message, index) => (
          <div
            key={`${message.role}-${index}`}
            className={cn(
              'rounded-lg px-3 py-2 text-sm whitespace-pre-wrap',
              message.role === 'user'
                ? 'ml-6 bg-primary text-primary-foreground'
                : 'mr-6 bg-muted text-foreground',
            )}
          >
            {message.content}
          </div>
        ))}

        {pending.length > 0 ? (
          <div className="space-y-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
            <div className="text-sm font-medium">{t.knowledge.proposed}</div>
            <ul className="space-y-1.5 text-xs text-muted-foreground">
              {pending.map((mutation, index) => (
                <li
                  key={`${mutation.action}-${index}`}
                  className="rounded border bg-background/80 p-2"
                >
                  <div className="font-medium text-foreground">
                    {mutation.action}
                    {mutation.label ? ` · ${mutation.label}` : ''}
                    {mutation.id ? `/${mutation.id}` : ''}
                  </div>
                  <div>{mutation.summary}</div>
                  {mutation.edge_type ? (
                    <div>
                      {mutation.from_label}/{mutation.from_id} -[{mutation.edge_type}]→{' '}
                      {mutation.to_label}/{mutation.to_id}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
            <div className="flex gap-2 pt-1">
              <Button type="button" size="sm" onClick={onConfirm} disabled={busy}>
                {t.common.confirm}
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={onReject} disabled={busy}>
                {t.common.reject}
              </Button>
            </div>
          </div>
        ) : null}
        </div>
      </ScrollArea>

      <div className="border-t p-3">
        <div className="flex gap-2">
          <Textarea
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            placeholder={t.knowledge.placeholder}
            className="min-h-[72px] resize-none"
            disabled={busy}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                onSend();
              }
            }}
          />
          <Button
            type="button"
            size="icon"
            aria-label={t.common.send}
            onClick={onSend}
            disabled={busy || !draft.trim()}
          >
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
